/* Keeps unfinished gifts between the gift builder and the custom blend page. */
(function () {
  'use strict';

  const root = window.Shopify?.routes?.root || '/';
  const prefix = 'th-gift-builder-draft:' + root;
  const clone = value => JSON.parse(JSON.stringify(value));
  const params = () => new URLSearchParams(window.location.search);
  const returnURL = token =>
    root + 'products/personalised-dried-fruit-gifts?th-gift-draft=' +
    encodeURIComponent(token) + '#th-step-3';

  function read(token) {
    let record;

    try {
      record = JSON.parse(sessionStorage.getItem(prefix + token) || 'null');
    } catch (_) {
      throw new Error(
        'Your gift draft could not be read. Return to the Gift Builder before adding this blend.'
      );
    }

    if (
      !record ||
      record.version !== 1 ||
      record.token !== token ||
      !record.draft ||
      !Array.isArray(record.draft.items) ||
      Date.now() - record.savedAt > 86400000
    ) {
      throw new Error(
        'This gift draft is no longer available in this tab. Return to the Gift Builder to continue.'
      );
    }

    return record;
  }

  function store(record) {
    record.savedAt = Date.now();

    try {
      sessionStorage.setItem(prefix + record.token, JSON.stringify(record));
    } catch (_) {
      throw new Error(
        'Browser storage is needed to keep your unfinished gift. Please enable it before leaving this page.'
      );
    }
  }

  async function check(record, cart) {
    const api = window.THGiftCart;

    if (!api) {
      throw new Error(
        'Gift protection did not load. Refresh before continuing.'
      );
    }

    cart = cart || await api.readCart();

    if (await api.pendingState(cart)) {
      throw new Error(
        'A previous gift save needs checking before this draft can continue.'
      );
    }

    if (api.fingerprint(cart) !== record.snapshot) {
      throw new Error(
        'Your cart gift changed while this blend was being built. Reopen the gift from your cart before continuing.'
      );
    }

    return cart;
  }

  function notice(builder, text) {
    let node = builder.querySelector('#th-gift-draft-notice');

    if (!node) {
      node = document.createElement('p');
      node.id = 'th-gift-draft-notice';
      node.setAttribute('role', 'status');
      node.style.cssText =
        'padding:14px;border:1px solid #dca94d;border-radius:8px;background:#fff9e8;';
      builder.insertBefore(node, builder.firstChild);
    }

    node.textContent = text;
  }

  async function begin(builder, link) {
    const draft = builder.thGiftDraft.get();

    if (!draft.recipient || draft.recipient.length > 80) {
      throw new Error(
        'Enter the recipient’s name before building their gift blend.'
      );
    }

    const api = window.THGiftCart;

    if (!api || typeof api.createDraft !== 'function') {
      throw new Error(
        'The matching gift builder update did not load. Refresh before continuing.'
      );
    }

    const cart = await api.readCart();

    if (await api.pendingState(cart)) {
      throw new Error(
        'A previous gift save needs checking before continuing.'
      );
    }

    const snapshot = api.fingerprint(cart);
    const gift = api.inspect(cart);

    if (
      draft.reference
        ? (
          !gift ||
          gift.reference !== draft.reference ||
          draft.snapshot !== snapshot
        )
        : Boolean(gift)
    ) {
      throw new Error(
        'Your gift changed. Reload the current gift before building this blend.'
      );
    }

    const token =
      builder.dataset.thGiftDraftToken ||
      'draft-' + Date.now() + '-' + Math.random().toString(36).slice(2);

    store({
      version: 1,
      token,
      snapshot,
      draft: clone(draft)
    });

    builder.dataset.thGiftDraftToken = token;

    const url = new URL(
      root + 'products/personalised-dried-fruit-mix',
      location.origin
    );

    url.searchParams.set('th-gift-intent', '1');
    url.searchParams.set('th-gift-draft', token);
    url.searchParams.set('th-gift-recipient', draft.recipient);

    const uid = link.dataset.thDraftBlend;

    if (uid) {
      const item = draft.items.find(
        item => item.uid === uid && item.blend
      );

      if (!item) {
        throw new Error(
          'This blend is no longer in your gift draft.'
        );
      }

      url.searchParams.set('th-draft-blend', uid);
      url.searchParams.set('_BYO_STATE', item.properties._BYO_STATE);

      if (item.sources?.length) {
        url.searchParams.set('_BYO_GROUP', item.properties._byo_group);
      }
    }

    window.location.assign(url.pathname + url.search);
  }

  async function addBlend(token, editingUid, line, carrier, product) {
    const record = read(token);
    await check(record);

    if (
      Number(line.quantity) !== 1 ||
      Number(carrier.id) !== 45956039901355 ||
      !Number.isSafeInteger(Number(carrier.quantity)) ||
      Number(carrier.quantity) < 1 ||
      Number(carrier.quantity) !== Number(line.properties?._byo_price_cents) ||
      !line.properties?._BYO_STATE ||
      !line.properties?._byo_group ||
      carrier.properties?._byo_group !== line.properties._byo_group
    ) {
      throw new Error(
        'Your blend and its price could not be verified together.'
      );
    }

    const item = {
      uid: line.properties._byo_group,
      variantId: Number(line.id),
      productTitle: product.title,
      productHandle: product.handle,
      variantTitle: line.properties['Bag size'],
      quantity: 1,
      price: Number(carrier.quantity),
      properties: clone(line.properties),
      sources: [],
      blend: true,
      carrierLine: clone(carrier)
    };

    if (editingUid) {
      const index = record.draft.items.findIndex(
        item => item.uid === editingUid && item.blend
      );

      if (index < 0) {
        throw new Error(
          'The blend you were editing is no longer in this gift draft.'
        );
      }

      record.draft.items[index] = item;
    } else {
      record.draft.items.push(item);
    }

    record.draft.reviewed = false;
    store(record);

    return returnURL(token);
  }

  async function restore(builder, token) {
    try {
      const record = read(token);
      await check(record);

      builder.thGiftDraft.hydrate(record.draft);
      builder.dataset.thGiftDraftToken = token;
      builder.dispatchEvent(new CustomEvent('th-gift-draft-restored'));

      notice(
        builder,
        'Your gift draft is ready. Finish the card/wrapping options and review the complete gift before adding or saving it.'
      );

      builder.querySelector('#th-step-3').scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } catch (error) {
      notice(builder, error.message);
    }
  }

  function boot() {
    const builder = document.getElementById('th-gift-builder-v2');

    if (!builder?.thGiftDraft || builder.dataset.thDraftBridgeReady) {
      return;
    }

    builder.dataset.thDraftBridgeReady = 'true';

    builder.addEventListener('click', event => {
      const link = event.target.closest(
        '[data-th-blend-gift], [data-th-draft-blend]'
      );

      if (!link) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (builder.dataset.thDraftLeaving) return;

      builder.dataset.thDraftLeaving = 'true';

      begin(builder, link)
        .catch(error => {
          notice(builder, error.message);
        })
        .finally(() => {
          delete builder.dataset.thDraftLeaving;
        });
    }, true);

    const token = params().get('th-gift-draft');

    if (token) restore(builder, token);
  }

  window.THGiftDraft = {
    read,
    check,
    addBlend,
    returnURL,

    clear(builder) {
      const token = builder?.dataset.thGiftDraftToken;

      if (token) {
        try {
          sessionStorage.removeItem(prefix + token);
        } catch (_) {}

        delete builder.dataset.thGiftDraftToken;
      }
    }
  };

  // Wait until all DOMContentLoaded listeners initialise the Gift Builder.
  const start = () => setTimeout(boot, 0);

  window.addEventListener('load', start);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;

    const builder = document.getElementById('th-gift-builder-v2');
    const token = params().get('th-gift-draft');

    if (builder?.thGiftDraft && token) {
      restore(builder, token);
    } else {
      start();
    }
  });
})();

/* Gift controls for the custom blend builder. */
(function () {
  'use strict';

  window.THBYOGift = {
    create(root, changed, reveal) {
      const api = window.THGiftCart;
      const params = new URLSearchParams(location.search);
      const group = params.get('_BYO_GROUP');
      const giftIntent = params.get('th-gift-intent') === '1';
      const draftToken = params.get('th-gift-draft');

      let handoff = null;
      let intentApplied = false;

      const existingBox = root.querySelector('#byo-gift-options');
      const box = existingBox || document.createElement('section');

      box.id = 'byo-gift-options';

      if (!existingBox) {
        box.innerHTML = `
          <details>
            <summary>🎁 Sending your blend as a gift?</summary>

            <p>
              One personalised gift per order.
              All products go to the same delivery address.
            </p>

            <p data-status role="status" aria-live="polite">
              Checking your gift options…
            </p>

            <fieldset>
              <legend>Choose a gift option</legend>

              <label>
                <input type="radio" name="byo-gift-choice" value="none" checked>
                No gift
              </label>

              <label data-new>
                <input type="radio" name="byo-gift-choice" value="new">
                Start a gift
              </label>

              <label data-existing hidden>
                <input type="radio" name="byo-gift-choice" value="existing">
                <span></span>
              </label>
            </fieldset>

            <div data-fields hidden>
              <label>
                Recipient's first name
                <input data-recipient type="text" maxlength="80" autocomplete="off">
              </label>

              <fieldset>
                <legend>Personal touch</legend>

                <label>
                  <input type="radio" name="byo-gift-service" value="card" checked>
                  Personalised card — $2
                </label>

                <label>
                  <input type="radio" name="byo-gift-service" value="wrap">
                  Gift wrapping + personalised card — $5
                </label>
              </fieldset>

              <label>Your card message<textarea data-message maxlength="250" rows="3"></textarea></label>

              <small>Up to 250 characters.</small>
            </div>

            <p data-price></p>

            <button type="button" data-refresh>Refresh gift options</button>

            <a data-cart>View cart</a>
            <a data-edit hidden>Edit existing gift</a>
          </details>
        `;
      }

      // Keep the gift choice after bag size and before fruit selection.
      root.querySelector('#byo-step-2').before(box);

      const disclosure = box.querySelector('details');
      const title = document.createElement('div');

      title.className = 'byo-gift-heading';
      title.textContent = '🎁 Sending this as a gift?';
      disclosure.querySelector('summary').replaceWith(title);

      const panel = document.createElement('div');

      panel.append(...disclosure.childNodes);
      disclosure.replaceWith(panel);

      const intro = document.createElement('p');

      intro.className = 'byo-gift-intro';
      intro.textContent =
        'Add a personalised card or gift wrapping to make it extra special.';

      title.after(intro);

      const seasonEnabled = () =>
        window.THGiftConfig?.christmasEnabled === true;

      const serviceFieldset = box
        .querySelector('[name="byo-gift-service"]')
        .closest('fieldset');

      const christmasLabel = document.createElement('label');

      christmasLabel.hidden = !seasonEnabled();
      christmasLabel.innerHTML =
        '<input type="radio" name="byo-gift-service" value="christmas"> Christmas presentation — $5 per blend bag. Festive clear packaging, ribbon, decorations and a Christmas tag.';

      serviceFieldset.appendChild(christmasLabel);

      const paperLabel = document.createElement('label');

      paperLabel.hidden = true;
      paperLabel.innerHTML =
        '<input type="checkbox" data-christmas-paper> Wrap the whole gift in festive paper — +$2 total';

      serviceFieldset.appendChild(paperLabel);

      const noGift = box.querySelector('[value="none"]');
      const noGiftText = document.createElement('strong');

      noGiftText.textContent = 'No gift option';
      noGift.closest('label').replaceChildren(noGift, noGiftText);

      if (!group && !draftToken && !giftIntent) {
        noGift.checked = true;
      }

      const journey = document.createElement('p');

      journey.hidden = true;
      journey.setAttribute('role', 'status');
      journey.style.cssText =
        'padding:14px;margin:0 0 14px;border:2px solid #e0aa4f;border-radius:10px;background:#fffaf1;font-weight:600;';

      panel.insertBefore(journey, panel.querySelector('fieldset'));

      const style = document.createElement('style');

      style.textContent = `
        #byo-gift-options {
          padding:20px!important;
          margin:20px 0 28px!important;
          border:2px solid #e0aa4f;
          border-radius:8px!important;
          background:#fffaf1;
          scroll-margin-top:140px;
        }

        #byo-gift-options .byo-gift-heading {
          font-size:1.08em;
          font-weight:700;
          margin-bottom:5px;
        }

        #byo-gift-options .byo-gift-intro {
          font-size:.92em;
          opacity:.8;
          line-height:1.45;
          margin:0 0 14px;
        }

        #byo-builder [data-byo-gift-status][hidden],
        #byo-builder [data-byo-gift-open][hidden] {
          display:none!important;
        }

        #byo-gift-options fieldset {
          border:0;
          padding:0;
          margin:12px 0;
        }

        #byo-gift-options legend {
          font-weight:600;
        }

        #byo-gift-options label {
          display:block;
          margin:10px 0;
        }

        #byo-gift-options input[type=text],
        #byo-gift-options textarea {
          display:block;
          width:100%;
          padding:10px;
          border:1px solid #aaa;
          border-radius:6px;
          font:inherit;
          font-size:16px;
        }

        #byo-gift-options [hidden] {
          display:none!important;
        }

        #byo-gift-options [data-refresh],
        #byo-gift-options a {
          display:inline-block;
          padding:8px;
          margin:4px;
          border:1px solid #b78a42;
          border-radius:6px;
          background:white;
          font:inherit;
          color:#443522;
        }
      `;

      document.head.appendChild(style);

      const newLabel = box.querySelector('[data-new]');
      const newChoice = newLabel.querySelector('input');
      const newText = document.createElement('span');

      newText.dataset.newLabel = 'true';
      newText.textContent = 'Start a gift';
      newLabel.replaceChildren(newChoice, newText);

      box.querySelector('[data-recipient]').value =
        (params.get('th-gift-recipient') || '').slice(0, 80);

      box.querySelector('[data-cart]').href =
        (api?.root || '/') + 'cart';

      let ready = false;
      let problem = '';
      let gift = null;
      let snapshot = '';
      let originalSnapshot = '';
      let original = null;
      let blendPrice = 0;

      const choice = () =>
        box.querySelector('[name="byo-gift-choice"]:checked')?.value || 'none';

      const service = () => {
        const value =
          box.querySelector('[name="byo-gift-service"]:checked')?.value ||
          'card';

        return (
          value === 'christmas' &&
          box.querySelector('[data-christmas-paper]').checked
        ) ? 'christmas-paper' : value;
      };

      const isChristmas = () => service().startsWith('christmas');

      const charge = () =>
        handoff && choice() !== 'none'
          ? 0
          : original?.properties?.['_Gift reference']
            ? 0
            : choice() === 'new'
              ? (
                isChristmas()
                  ? 500 + (service() === 'christmas-paper' ? 200 : 0)
                  : service() === 'wrap'
                    ? 500
                    : 200
              )
              : (
                choice() === 'existing' &&
                gift?.service?.startsWith('christmas')
              )
                ? 500
                : choice() === 'existing' && gift?.service === 'wrap'
                  ? 200
                  : 0;

      const recipient = () =>
        handoff && choice() !== 'none'
          ? handoff.draft.recipient
          : choice() === 'existing'
            ? gift?.recipient || ''
            : box.querySelector('[data-recipient]').value.trim();

      const status = text => {
        box.querySelector('[data-status]').textContent = text;
      };

      function paint() {
        box.querySelector('[data-fields]').hidden =
          choice() !== 'new' || Boolean(handoff);

        const seasonal = isChristmas();

        christmasLabel.hidden = !seasonEnabled();
        paperLabel.hidden = !seasonal;

        const message = box.querySelector('[data-message]');

        message.placeholder = seasonal
          ? 'Optional Christmas gift-tag message…'
          : 'Write your personalised card message…';

        message.maxLength = seasonal ? 35 : 250;

        const messageLabel = message.closest('label');

        if (messageLabel.firstChild.nodeType === 3) {
          messageLabel.firstChild.textContent = seasonal
            ? 'Christmas tag message (optional)'
            : 'Your card message';
        }

        box.querySelector('[data-fields] small').textContent = seasonal
          ? 'Optional handwritten tag message, up to 35 characters.'
          : 'Up to 250 characters.';

        const cents = charge();

        let text = choice() === 'none'
          ? 'Your blend will be added as an ordinary purchase.'
          : handoff
            ? (
              'You’re creating a blend for ' +
              handoff.draft.recipient +
              '’s gift. Return to the Gift Builder to choose card/wrapping, write your message and review the complete gift. Nothing is added to your cart yet.'
            )
            : original?.properties?.['_Gift reference']
              ? 'Your blend stays in the same gift. Its card and wrapping charges stay the same.'
              : choice() === 'existing'
                ? (
                  gift?.service?.startsWith('christmas')
                    ? 'One blend bag: $5 individual Christmas presentation. Whole-gift paper, if selected, remains $2 total.'
                    : cents
                      ? 'One blend bag: $2 additional wrapping.'
                      : 'Added to the existing card-only gift. No additional gifting charge.'
                )
                : (
                  'One blend bag: ' +
                  (
                    seasonal
                      ? (
                        '$5 individual Christmas presentation' +
                        (
                          service() === 'christmas-paper'
                            ? ' + $2 whole-gift festive paper.'
                            : '.'
                        )
                      )
                      : cents === 500
                        ? '$5 for wrapping + personalised card.'
                        : '$2 for a personalised card.'
                  )
                );

        box.querySelector('[data-price]').textContent =
          text +
          ' Blend: $' +
          (blendPrice / 100).toFixed(2) +
          (
            handoff && choice() !== 'none'
              ? '. Gifting charges will be reviewed in the Gift Builder.'
              : (
                '. Total when added: $' +
                ((blendPrice + cents) / 100).toFixed(2) +
                '.'
              )
          );

        title.textContent = handoff
          ? '🎁 Creating a blend for ' + handoff.draft.recipient + '’s gift'
          : '🎁 Sending this as a gift?';

        intro.textContent = handoff
          ? 'Choose the bag size and fruits for their blend, then return to the Gift Builder to add more items or finish their gift.'
          : 'Add a personalised card or gift wrapping to make it extra special.';

        box.querySelector('[data-cart]').hidden = Boolean(handoff);

        journey.hidden = true;

        if (handoff) {
          journey.textContent =
            'You’re creating a blend for ' +
            handoff.draft.recipient +
            '’s gift. Choose your fruits, then select Add blend & return to gift.';
        }

        box
          .querySelector('[name="byo-gift-choice"]')
          .closest('fieldset')
          .hidden = Boolean(handoff);

        const label = problem && choice() !== 'none'
          ? 'Gift needs checking: ' + problem
          : handoff
            ? (
              '🎁 Blend for ' +
              handoff.draft.recipient +
              ' — return to the Gift Builder when ready.'
            )
            : choice() === 'none'
              ? ''
              : problem
                ? 'Gift needs checking: ' + problem
                : !ready
                  ? 'Checking gift options…'
                  : recipient()
                    ? '🎁 Gift for ' + recipient()
                    : '🎁 Gift selected — enter the recipient and card message';

        root.querySelectorAll('[data-byo-gift-status]').forEach(node => {
          node.textContent = label;
          node.hidden = !label;
        });

        root.querySelectorAll('[data-byo-gift-open]').forEach(button => {
          button.hidden = Boolean(handoff) || choice() === 'none';
          button.disabled = !ready;
          button.textContent = 'Change gift options';
        });
      }

      async function refresh() {
        ready = false;
        problem = '';

        box.querySelectorAll('fieldset').forEach(field => {
          field.disabled = true;
        });

        status('Checking your gift options…');

        try {
          if (!api || api.version !== 7) {
            throw new Error(
              'Gift protection did not load. Refresh this page before adding a gift.'
            );
          }

          const cart = await api.readCart();

          if (await api.pendingState(cart)) {
            throw new Error(
              'A previous gift change needs checking. Please review your cart or finish the saved edit in the gift builder.'
            );
          }

          gift = api.inspect(cart);
          snapshot = api.fingerprint(cart);

          if (draftToken) {
            if (!window.THGiftDraft) {
              throw new Error(
                'Gift draft protection did not load. Refresh before continuing.'
              );
            }

            handoff = window.THGiftDraft.read(draftToken);
            await window.THGiftDraft.check(handoff, cart);
          }

          const pair = group
            ? cart.items.filter(item => item.properties?._byo_group === group)
            : [];

          original = pair.find(item => !api.isCarrier(item)) || null;

          if (
            group &&
            (
              pair.length !== 2 ||
              !original ||
              !pair.some(api.isCarrier)
            )
          ) {
            throw new Error(
              'The original blend and its price could not be found together. Reopen the blend from your cart.'
            );
          }

          originalSnapshot = JSON.stringify(
            pair.map(item => [
              item.key,
              item.quantity,
              item.properties || {}
            ])
          );

          const locked = Boolean(
            original?.properties?.['_Gift reference']
          );

          box.querySelector('[data-new]').hidden = Boolean(gift);
          box.querySelector('[data-existing]').hidden = !gift;
          box.querySelector('[value="none"]').disabled = locked;

          if (gift) {
            box.querySelector('[data-existing] span').textContent =
              'Add this blend to ' + gift.recipient + "'s gift";
          }

          if (locked) {
            box.querySelector('[value="existing"]').checked = true;
          } else if (
            choice() === 'existing' && !gift ||
            choice() === 'new' && gift
          ) {
            box.querySelector('[value="none"]').checked = true;
          }

          const edit = box.querySelector('[data-edit]');

          edit.hidden = !gift;

          if (gift) {
            edit.href =
              api.root +
              'products/personalised-dried-fruit-gifts?th-edit-gift=' +
              encodeURIComponent(gift.reference);
          }

          status(
            locked
              ? 'Editing a blend already included in your gift. To remove it from the gift, use the gift editor.'
              : gift
                ? 'Your existing gift is for ' + gift.recipient + '.'
                : 'Choose a personalised card or optional wrapping below.'
          );

          if (
            (draftToken || giftIntent && !group) &&
            !intentApplied
          ) {
            box.querySelector(
              gift ? '[value="existing"]' : '[value="new"]'
            ).checked = true;

            intentApplied = true;
          }

          if (handoff) {
            box.querySelector('[data-recipient]').value =
              handoff.draft.recipient;

            if (gift) {
              box.querySelector('[data-existing] span').textContent =
                'Add this blend to ' +
                handoff.draft.recipient +
                "'s gift draft";
            } else {
              box.querySelector('[data-new-label]').textContent =
                'Add this blend to ' +
                handoff.draft.recipient +
                "'s gift draft";
            }

            edit.hidden = false;
            edit.href = window.THGiftDraft.returnURL(draftToken);
            edit.textContent = 'Return without adding this blend';

            status(
              'This blend is part of ' +
              handoff.draft.recipient +
              '’s unfinished gift. Return to the Gift Builder when your blend is ready.'
            );

            box.querySelector(
              gift ? '[value="existing"]' : '[value="new"]'
            ).checked = true;
          }

          ready = true;
        } catch (error) {
          problem = error.message;
          status(problem);
        }

        box.querySelectorAll('fieldset').forEach(field => {
          field.disabled = !ready;
        });

        paint();
        changed();
      }

      box.addEventListener('change', () => {
        paint();
        changed();
      });

      box.addEventListener('input', () => {
        paint();
        changed();
      });

      box.querySelector('[data-refresh]').addEventListener('click', refresh);

      root.querySelectorAll(
        '.byo-summary-footer, .sheet-footer'
      ).forEach(footer => {
        const summary = document.createElement('p');

        summary.dataset.byoGiftStatus = 'true';
        summary.style.cssText =
          'margin:0 0 10px;font-weight:600;color:#285b32;';
        summary.setAttribute('aria-live', 'polite');

        footer.insertBefore(
          summary,
          footer.querySelector('.byo-add-btn')
        );

        const button = document.createElement('button');

        button.type = 'button';
        button.dataset.byoGiftOpen = 'true';
        button.style.cssText =
          'display:block;margin:0 0 10px;padding:0;border:0;background:none;color:#285b32;text-decoration:underline;cursor:pointer;font:inherit;font-size:13px;';

        button.addEventListener('click', () => {
          if (!ready) return;

          paint();
          changed();
          reveal();

          box.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });

          if (!gift && !handoff) {
            box.querySelector('[data-recipient]').focus({
              preventScroll: true
            });
          }
        });

        footer.insertBefore(
          button,
          footer.querySelector('.byo-add-btn')
        );
      });

      paint();

      const loading = refresh();

      return {
        charge,

        actionLabel(editing) {
          if (handoff && choice() !== 'none') {
            return editing
              ? 'Update blend & return to gift'
              : 'Add blend & return to gift';
          }

          return choice() === 'none'
            ? (editing ? 'Update Cart' : 'Add to Cart')
            : (editing ? 'Update gift blend' : 'Add blend to gift');
        },

        price(cents) {
          blendPrice = cents;
          paint();
        },

        async submit(line, carrier) {
          if (
            !group &&
            !draftToken &&
            !giftIntent &&
            choice() === 'none'
          ) {
            return false;
          }

          await loading;

          if (!ready) {
            throw new Error(
              problem || 'Please refresh the gift options.'
            );
          }

          if (
            !original?.properties?.['_Gift reference'] &&
            choice() === 'none'
          ) {
            return false;
          }

          if (handoff) {
            return window.THGiftDraft.addBlend(
              draftToken,
              params.get('th-draft-blend'),
              line,
              carrier,
              {
                title: root.dataset.productTitle || 'Personalised blend',
                handle:
                  root.dataset.productHandle ||
                  'personalised-dried-fruit-mix'
              }
            );
          }

          if (
            choice() === 'new' &&
            isChristmas() &&
            !seasonEnabled()
          ) {
            reveal();

            box.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });

            throw new Error(
              'Christmas gifting is currently unavailable. Choose a regular gift option.'
            );
          }

          if (
            choice() === 'new' &&
            box.querySelector('[data-recipient]').value.trim().length > 80
          ) {
            reveal();
            box.querySelector('[data-recipient]').focus();

            throw new Error(
              'Please keep the recipient name within 80 characters.'
            );
          }

          if (
            choice() === 'new' &&
            (
              !box.querySelector('[data-recipient]').value.trim() ||
              (
                !isChristmas() &&
                !box.querySelector('[data-message]').value.trim()
              ) ||
              box.querySelector('[data-message]').value.length >
                (isChristmas() ? 35 : 250)
            )
          ) {
            reveal();

            box.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });

            throw new Error(
              isChristmas()
                ? 'Enter the recipient and keep the optional Christmas tag message within 35 characters.'
                : 'Please enter the gift recipient and personalised card message.'
            );
          }

          const saved = await api.saveBlend({
            line,
            carrier,
            choice: choice(),
            service: service(),
            snapshot,
            originalSnapshot,
            editGroup: group,
            recipient: box.querySelector('[data-recipient]').value,
            message: box.querySelector('[data-message]').value,
            title: root.dataset.productTitle || 'Personalised blend',
            handle:
              root.dataset.productHandle ||
              'personalised-dried-fruit-mix'
          });

          const savedGift = api.inspect(saved);

          return (
            api.root +
            'cart#th-gift-review=' +
            encodeURIComponent(savedGift.reference)
          );
        }
      };
    }
  };
})();